import { format, parseISO } from 'date-fns';
import moment from 'moment-timezone';
import { v4 as uuid } from 'uuid';

import { getWorstDevice } from '@/components/StatusIcon/statusMapping';
import type { DeviceStatus } from '@/types';

import { Address } from '../types';
import type { Unit } from '../Units/types';
import type { UnitHourlyUsage, UnitUsage } from '../Usage/types';
import { addCreate, naturalCompare } from '../util';

/**
 * Sort array of objects, each containing an `index` property
 *
 * @param  {object} a - First item to sort
 * @param  {object} b - Second item to sort
 * @returns {number} - Sort order for a and b
 */
export const sortByIndex = (a: { index: number }, b: { index: number }) => {
  let sort = 0;

  if (a.index < b.index) {
    sort = -1;
  } else if (b.index < a.index) {
    sort = 1;
  }

  return sort;
};

/**
 * Create Units
 *
 * @param  {number} numberOfUnits   - Number of units to create
 * @param  {number} defaultPipeSize - Default pipe size
 * @param  {number} defaultPipeType - Default pipe type
 * @returns {object[]} - Array of units
 */
export const createUnits = (numberOfUnits: number): Unit[] =>
  Array.from({ length: numberOfUnits }, (_, idx) => ({
    id: uuid(),
    name: String(idx + 1),
    index: idx,
    devices: [],
    deviceIds: [],
    address: { street: '', street2: '', city: '', state: '', zip: '' },
    unit_flags: [],
  }));

/**
 * Determine if a single unit is valid (based on user entered data)
 *
 * @param  {object}  unit - Unit to validate
 * @returns {boolean} - Wether or not the unit is valid
 */
export const isUnitValid = (unit: Unit) => {
  // .trim() so whitespace-only names (e.g. "   ") are also rejected.
  return Boolean(unit.name && unit.name.trim() !== '');
};

/**
 * Get index of next section
 *
 * @param  {number}   curIndex - Current index
 * @param  {number}   maxIndex - Max index of sections
 * @returns {number} - Index of next section
 */
export const getNextIndex = (curIndex: number, maxIndex: number): number =>
  curIndex + 1 < maxIndex ? curIndex + 1 : maxIndex;

/**
 * Get index of previous section
 *
 * @param  {number}   curIndex - Current index
 * @param  {number}   minIndex - Minimum index of sections
 * @returns {number} - Index of previous section
 */
export const getPrevIndex = (curIndex: number, minIndex: number = 0) =>
  curIndex - 1 > minIndex ? curIndex - 1 : minIndex;

export const addressIsValid = (address: Address): boolean => {
  // .trim() so whitespace-only entries (e.g. "   ") are also rejected.
  return Boolean(
    address.street?.trim() && address.city?.trim() && address.state?.trim() && address.zip?.trim(),
  );
};

type CSVMetadata = {
  start: number | string;
  end: number | string;
  timezone: string;
  propertyName?: string;
  propertyAddress?: Address;
};

type EstimatedByBucket = { [bucketKey: string]: number };

/**
 * Aggregate each unit's estimated buckets by a row key (local date string for the
 * daily CSV, epoch-second string for the hourly CSV). Estimated usage exports as
 * SEPARATE columns, never merged into the metered numbers.
 */
const aggregateUnitEstimates = (
  rawUsage: {
    unit_name: string;
    estimated_bucket_volumes?: { bucket_start: number; volume: number }[];
  }[],
  bucketKey: (bucketStart: number) => string,
): { byUnit: { [unitName: string]: EstimatedByBucket }; total: EstimatedByBucket } => {
  const byUnit: { [unitName: string]: EstimatedByBucket } = {};
  const total: EstimatedByBucket = {};
  rawUsage.forEach((unit) => {
    (unit.estimated_bucket_volumes ?? []).forEach((entry) => {
      const key = bucketKey(entry.bucket_start);
      byUnit[unit.unit_name] = byUnit[unit.unit_name] ?? {};
      byUnit[unit.unit_name][key] = (byUnit[unit.unit_name][key] ?? 0) + entry.volume;
      total[key] = (total[key] ?? 0) + entry.volume;
    });
  });
  return { byUnit, total };
};

export const generateUsageCSV = (
  rawUsage: UnitUsage[] | undefined,
  metadata: CSVMetadata,
): string => {
  if (!rawUsage) {
    return '';
  }

  const usageByDate = {
    TOTAL: {},
    ...rawUsage.reduce((acc, cur) => ({ ...acc, [cur.unit_name]: {} }), {}),
  };

  rawUsage.forEach((unit) => {
    unit.devices.forEach((device) => {
      device.daily_usages.forEach((usageData) => {
        // Use volume directly from backend (already rounded to 2 decimals)
        // DO NOT apply Math.round() here; it causes double-rounding errors
        addCreate(usageByDate[unit.unit_name], usageData.date, usageData.volume);
        addCreate(usageByDate.TOTAL, usageData.date, usageData.volume);
      });
    });
  });

  let propertyInfo = '';
  if (metadata.propertyName) {
    propertyInfo += `Property:,"${metadata.propertyName}"\n`;
  }
  if (metadata.propertyAddress) {
    const addr = metadata.propertyAddress;
    const line = [addr.street, addr.street2, addr.city, addr.state, addr.zip]
      .filter(Boolean)
      .join(', ');
    propertyInfo += `Address:,"${line}"\n`;
  }
  if (propertyInfo) {
    propertyInfo += '\n';
  }

  // Estimated usage keyed by local date, per unit and property-wide; separate
  // columns that appear only when the range actually has estimates
  const { byUnit: estimatedByUnit, total: estimatedTotals } = aggregateUnitEstimates(
    rawUsage,
    (bucketStart) => moment.tz(bucketStart * 1000, metadata.timezone).format('YYYY-MM-DD'),
  );
  const unitsWithEstimates = Object.keys(estimatedByUnit).sort(naturalCompare);
  const hasEstimates = unitsWithEstimates.length > 0;

  const propertyEstimatedHeader = hasEstimates ? ',Property Estimated (gal)' : '';
  const estimatedUnitHeaders = unitsWithEstimates
    .map((unitName) => `,"${unitName} Estimated (gal)"`)
    .join('');

  const headers = `Date (${
    metadata.timezone
  }),Property Total (gal)${propertyEstimatedHeader},"${Object.keys(usageByDate)
    .filter((k) => k !== 'TOTAL')
    .sort(naturalCompare)
    .join(' (gal)","')} (gal)"${estimatedUnitHeaders}, ,start: ${metadata.start},end: ${
    metadata.end
  },timezone: ${metadata.timezone}\n`;

  // Rows cover every date with metered usage plus estimate-only dates
  const rowDates = Array.from(
    new Set([...Object.keys(usageByDate.TOTAL), ...Object.keys(estimatedTotals)]),
  ).sort();

  const estimatedTotalsCell = hasEstimates
    ? `,${Object.values(estimatedTotals)
        .reduce((acc, cur) => acc + cur, 0)
        .toFixed(2)}`
    : '';
  const estimatedUnitTotalsCells = unitsWithEstimates
    .map(
      (unitName) =>
        `,${Object.values(estimatedByUnit[unitName])
          .reduce((acc, cur) => acc + cur, 0)
          .toFixed(2)}`,
    )
    .join('');

  const totalsRow =
    rowDates.length &&
    `Total ${format(parseISO(rowDates[0]), 'M/d/yyyy')} - ${format(
      parseISO(rowDates[rowDates.length - 1]),
      'M/d/yyyy',
    )},${Object.values(usageByDate.TOTAL as number[])
      .reduce((acc: number, cur: number) => acc + cur, 0)
      .toFixed(2)}${estimatedTotalsCell},${Object.keys(usageByDate)
      .filter((unitId) => unitId !== 'TOTAL')
      .sort(naturalCompare)
      .map((unitId) =>
        Object.values(usageByDate[unitId] as number[])
          .reduce((acc: number, cur) => acc + cur, 0)
          .toFixed(2),
      )
      .join(',')}${estimatedUnitTotalsCells}\n`;

  const body = rowDates
    .map((dateStr) => {
      const estimatedTotalCell = hasEstimates
        ? `,${(estimatedTotals[dateStr] ?? 0).toFixed(2)}`
        : '';
      const estimatedUnitCells = unitsWithEstimates
        .map((unitName) => `,${(estimatedByUnit[unitName][dateStr] ?? 0).toFixed(2)}`)
        .join('');
      return `${format(parseISO(dateStr), 'M/d/yyyy')},${Number(
        usageByDate.TOTAL[dateStr] ?? 0,
      ).toFixed(2)}${estimatedTotalCell},${Object.keys(usageByDate)
        .filter((k) => k !== 'TOTAL')
        .sort(naturalCompare)
        .map((k) =>
          !isNaN(usageByDate[k][dateStr]) ? Number(usageByDate[k][dateStr]).toFixed(2) : '0.00',
        )
        .join(',')}${estimatedUnitCells}`;
    })
    .join('\n');

  return `${propertyInfo}${headers}${totalsRow || ''}${body}`;
};

/**
 * Property-level hourly CSV. One row per hour, columns per unit. Times rendered in the
 * property's timezone (via moment-timezone) so the export matches what the customer sees
 * in the webapp regardless of where they're physically located.
 */
export const generateHourlyUsageCSV = (
  rawUsage: UnitHourlyUsage[] | undefined,
  metadata: CSVMetadata,
): string => {
  if (!rawUsage) {
    return '';
  }

  const usageByHour: { TOTAL: { [hour: string]: number } } & {
    [unitName: string]: { [hour: string]: number };
  } = {
    TOTAL: {},
    ...rawUsage.reduce((acc, cur) => ({ ...acc, [cur.unit_name]: {} }), {}),
  };

  rawUsage.forEach((unit) => {
    unit.devices.forEach((device) => {
      device.hourly_usages.forEach((bucket) => {
        const hourKey = String(bucket.time);
        addCreate(usageByHour[unit.unit_name], hourKey, bucket.volume);
        addCreate(usageByHour.TOTAL, hourKey, bucket.volume);
      });
    });
  });

  let propertyInfo = '';
  if (metadata.propertyName) {
    propertyInfo += `Property:,"${metadata.propertyName}"\n`;
  }
  if (metadata.propertyAddress) {
    const addr = metadata.propertyAddress;
    const line = [addr.street, addr.street2, addr.city, addr.state, addr.zip]
      .filter(Boolean)
      .join(', ');
    propertyInfo += `Address:,"${line}"\n`;
  }
  if (propertyInfo) {
    propertyInfo += '\n';
  }

  // Estimated usage keyed by the hour bucket's epoch_second; separate columns
  // matching the daily property CSV behavior
  const { byUnit: estimatedByUnit, total: estimatedTotals } = aggregateUnitEstimates(
    rawUsage,
    (bucketStart) => String(bucketStart),
  );
  const unitsWithEstimates = Object.keys(estimatedByUnit).sort(naturalCompare);
  const hasEstimates = unitsWithEstimates.length > 0;

  const propertyEstimatedHeader = hasEstimates ? ',Property Estimated (gal)' : '';
  const estimatedUnitHeaders = unitsWithEstimates
    .map((unitName) => `,"${unitName} Estimated (gal)"`)
    .join('');

  const headers = `Hour (${
    metadata.timezone
  }),Property Total (gal)${propertyEstimatedHeader},"${Object.keys(usageByHour)
    .filter((k) => k !== 'TOTAL')
    .sort(naturalCompare)
    .join(' (gal)","')} (gal)"${estimatedUnitHeaders}, ,start: ${metadata.start},end: ${
    metadata.end
  },timezone: ${metadata.timezone}\n`;

  const sortedHours = Array.from(
    new Set([...Object.keys(usageByHour.TOTAL), ...Object.keys(estimatedTotals)]),
  ).sort((a, b) => Number(a) - Number(b));

  const formatHour = (epochSecond: number): string =>
    moment.tz(epochSecond * 1000, metadata.timezone).format('M/D/YYYY h:mm A');

  const estimatedTotalsCell = hasEstimates
    ? `,${Object.values(estimatedTotals)
        .reduce((acc, cur) => acc + cur, 0)
        .toFixed(2)}`
    : '';
  const estimatedUnitTotalsCells = unitsWithEstimates
    .map(
      (unitName) =>
        `,${Object.values(estimatedByUnit[unitName])
          .reduce((acc, cur) => acc + cur, 0)
          .toFixed(2)}`,
    )
    .join('');

  const totalsRow =
    sortedHours.length &&
    `Total ${formatHour(Number(sortedHours[0]))} - ${formatHour(
      Number(sortedHours[sortedHours.length - 1]),
    )},${(Object.values(usageByHour.TOTAL) as number[])
      .reduce((acc, cur) => acc + cur, 0)
      .toFixed(2)}${estimatedTotalsCell},${Object.keys(usageByHour)
      .filter((unitId) => unitId !== 'TOTAL')
      .sort(naturalCompare)
      .map((unitId) =>
        (Object.values(usageByHour[unitId]) as number[])
          .reduce((acc, cur) => acc + cur, 0)
          .toFixed(2),
      )
      .join(',')}${estimatedUnitTotalsCells}\n`;

  const body = sortedHours
    .map((hourKey) => {
      const estimatedTotalCell = hasEstimates
        ? `,${(estimatedTotals[hourKey] ?? 0).toFixed(2)}`
        : '';
      const estimatedUnitCells = unitsWithEstimates
        .map((unitName) => `,${(estimatedByUnit[unitName][hourKey] ?? 0).toFixed(2)}`)
        .join('');
      return `${formatHour(Number(hourKey))},${Number(usageByHour.TOTAL[hourKey] ?? 0).toFixed(
        2,
      )}${estimatedTotalCell},${Object.keys(usageByHour)
        .filter((k) => k !== 'TOTAL')
        .sort(naturalCompare)
        .map((k) =>
          !isNaN(usageByHour[k][hourKey]) ? Number(usageByHour[k][hourKey]).toFixed(2) : '0.00',
        )
        .join(',')}${estimatedUnitCells}`;
    })
    .join('\n');

  return `${propertyInfo}${headers}${totalsRow || ''}${body}`;
};

export const getUnitStatus = (unit: Unit): DeviceStatus => {
  if (!unit?.devices?.length) return 'NO_DEVICES';
  const worst = getWorstDevice(unit.devices);
  return worst?.device_status ?? 'OKAY';
};
